import { useMemo } from 'react';
import type { Boundary } from '../../engine/battlefield/boundary';
import type { Camera } from '../../engine/battlefield/projection';
import type { VoxelGrid } from '../../engine/battlefield/voxels';
import { gridLines, groundPathFor, outlinePoints } from './sceneDrawing';

/**
 * The floor both renderers stand on: the ground (holes punched for what was
 * dug), the metre grid and the arena's outline. Each renderer gives its own
 * classes; the geometry and the `battlefield-ground` test id are shared. With
 * `lines` off (the GPU map draws the grid and the outline: perf overhaul 2b)
 * only the ground is drawn - see-through there, the pointer's target.
 */
export function SceneFloor({
  boundary,
  camera,
  voxels,
  showGrid,
  classes,
  gridMask,
  lines = true,
}: {
  boundary: Boundary;
  camera: Camera;
  voxels: VoxelGrid | null;
  showGrid: boolean;
  classes: { ground: string; grid: string; boundary: string };
  /** A mask over the grid alone (the display's fade with distance); none on the classic map. */
  gridMask?: string;
  /** Draw the grid and the outline (off while the GPU map draws them). */
  lines?: boolean;
}) {
  const outline = useMemo(() => (lines ? outlinePoints(boundary, camera) : null), [lines, boundary, camera]);
  const grid = useMemo(
    () => (lines && showGrid ? gridLines(boundary, camera) : []),
    [lines, boundary, camera, showGrid],
  );
  const groundPath = useMemo(() => groundPathFor(boundary, camera, voxels), [boundary, camera, voxels]);
  return (
    <>
      <path className={classes.ground} d={groundPath} fillRule="evenodd" data-testid="battlefield-ground" />
      <g mask={gridMask}>
        {grid.map((line) => (
          <line key={line.key} className={classes.grid} x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2} />
        ))}
      </g>
      {outline !== null && <polygon className={classes.boundary} points={outline} />}
    </>
  );
}
